import { TFunction } from '@frontend/i18n';
import { MediaValidationResult, MediaValidationRules } from './types';

const formatBytes = (bytes: number): string => {
  if (bytes >= 1_000_000) return `${Math.round(bytes / 1_000_000)}MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)}KB`;
  return `${bytes}B`;
};

/** Matches a file against a single accept entry: MIME (`image/png`), wildcard MIME (`image/*`), or extension (`.png`). */
const matchesAcceptEntry = (file: File, entry: string): boolean => {
  const normalized = entry.trim().toLowerCase();
  if (!normalized) return false;

  if (normalized.startsWith('.')) {
    return file.name.toLowerCase().endsWith(normalized);
  }

  if (normalized.endsWith('/*')) {
    const group = normalized.slice(0, normalized.indexOf('/'));
    return file.type.toLowerCase().startsWith(`${group}/`);
  }

  return file.type.toLowerCase() === normalized;
};

/** Validate a single file against type and size rules. */
export const validateMediaFile = (
  file: File,
  rules: MediaValidationRules = {},
  t: TFunction
): MediaValidationResult => {
  const { accept, maxSizeBytes } = rules;

  if (accept?.length && !accept.some((entry) => matchesAcceptEntry(file, entry))) {
    return {
      valid: false,
      message: t('File type not allowed. Accepted: {{types}}', {
        types: accept.join(', '),
        ns: 'digital-forms-experience',
      }),
    };
  }

  if (maxSizeBytes !== null && maxSizeBytes !== undefined && file.size > maxSizeBytes) {
    return {
      valid: false,
      message: t('File exceeds the {{limit}} limit', {
        limit: formatBytes(maxSizeBytes),
        ns: 'digital-forms-experience',
      }),
    };
  }

  return { valid: true };
};

/** Number of additional files that can be added before hitting `maxFiles`. Infinity when unbounded. */
export const getRemainingSlots = (existingCount: number, rules: MediaValidationRules = {}): number => {
  if (!rules.maxFiles) return Number.POSITIVE_INFINITY;
  return Math.max(0, rules.maxFiles - existingCount);
};

/** Validate that adding `incomingCount` files stays within `maxFiles`. */
export const validateFieldCount = (
  existingCount: number,
  incomingCount: number,
  rules: MediaValidationRules = {},
  t: TFunction
): MediaValidationResult => {
  if (rules.maxFiles && existingCount + incomingCount > rules.maxFiles) {
    return {
      valid: false,
      message: t('You can upload at most {{count}} file(s)', { count: rules.maxFiles, ns: 'digital-forms-experience' }),
    };
  }
  return { valid: true };
};

const MB = 1024 * 1024;

/** Default rule presets, consumed at the input level. Callers may override per field. */
export const IMAGE_UPLOAD_RULES: MediaValidationRules = {
  accept: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
  maxSizeBytes: 12 * MB,
};

export const PDF_UPLOAD_RULES: MediaValidationRules = {
  accept: ['application/pdf'],
  maxSizeBytes: 25 * MB,
};

export const CARD_CAPTURE_RULES: MediaValidationRules = {
  accept: ['image/png', 'image/jpeg', 'image/jpg'],
  maxSizeBytes: 10 * MB,
};
